import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import 'react-quill/dist/quill.snow.css';
import {
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Eye,
  PlayCircle,
  PauseCircle,
  RefreshCw,
} from 'lucide-react';
import { policyService, acknowledgmentService } from '../../api/services/complianceService';
import { useAuth } from '../../context/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDate } from '../../utils/stringUtils';
import PaginationControls from '../../components/common/Pagination/PaginationControls';

const formatLabel = (value) => {
  if (!value && value !== 0) return value;
  return String(value)
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
    .join(' ')
    .trim();
};

const RICH_TEXT_SANITIZE_OPTIONS = {
  ADD_ATTR: ['style', 'class', 'target', 'rel'],
};

const sanitizeRichTextHtml = (html) =>
  DOMPurify.sanitize(html, RICH_TEXT_SANITIZE_OPTIONS);

const prepareDescriptionForDisplay = (description) => {
  if (!description || !String(description).trim()) {
    return '<p class="text-gray-500">No description available</p>';
  }
  const trimmed = String(description).trim();
  if (/<[a-z][\s\S]*>/i.test(trimmed)) {
    return sanitizeRichTextHtml(trimmed);
  }
  const escaped = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>');
  return `<p>${escaped}</p>`;
};

const RICH_TEXT_DISPLAY_CLASS =
  'text-sm text-gray-700 leading-relaxed break-words ' +
  '[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:text-gray-900 ' +
  '[&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:text-gray-900 ' +
  '[&_h3]:text-lg [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:text-gray-900 ' +
  '[&_h4]:text-base [&_h4]:font-bold [&_h4]:mb-2 [&_h4]:text-gray-900 ' +
  '[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-4 [&_ul]:mt-2 ' +
  '[&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-4 [&_ol]:mt-2 ' +
  '[&_li]:mb-1 [&_li]:pl-1 ' +
  '[&_p]:mb-4 [&_p]:leading-relaxed [&_p:last-child]:mb-0 ' +
  '[&_strong]:font-bold [&_strong]:text-gray-900 ' +
  '[&_em]:italic [&_u]:underline [&_s]:line-through ' +
  '[&_blockquote]:border-l-4 [&_blockquote]:border-gray-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4 ' +
  '[&_a]:text-[#0F2942] [&_a]:underline [&_a]:hover:text-blue-800 ' +
  '[&_pre]:bg-gray-50 [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-4 ' +
  '[&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono';

const getPlainTextFromHtml = (html = '') => {
  if (typeof document === 'undefined') {
    return String(html).replace(/<[^>]*>/g, '');
  }
  const container = document.createElement('div');
  container.innerHTML = html;
  return container.textContent || container.innerText || '';
};

const PoliciesCompliance = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  // Pagination state
  const [policiesCurrentPage, setPoliciesCurrentPage] = useState(1);
  const policiesItemsPerPage = 10;
  const [policiesTotalPages, setPoliciesTotalPages] = useState(1);
  const [policiesTotal, setPoliciesTotal] = useState(0);

  // Fetch KPI stats (all-pages totals, not just current page)
  const { data: statsResponse, refetch: refetchStats } = useQuery({
    queryKey: ['policies', 'my-stats'],
    queryFn: () => policyService.getMyPolicyStats(),
    staleTime: 0,
    retry: 2,
  });

  const kpiStats = React.useMemo(() => {
    if (!statsResponse) return { total: 0, acknowledged: 0, pending: 0 };
    const d = statsResponse.data || statsResponse;
    return {
      total: d.total ?? 0,
      acknowledged: d.acknowledged ?? 0,
      pending: d.pending ?? 0,
    };
  }, [statsResponse]);

  // Fetch policies from API using React Query
  const {
    data: policiesResponse,
    isLoading: policiesLoading,
    error: policiesError
  } = useQuery({
    queryKey: ['policies', 'active', policiesCurrentPage],
    queryFn: async () => {

      const response = await policyService.getPolicies({
        status: 'active',
        page: policiesCurrentPage,
        limit: policiesItemsPerPage
      });


      if (response?.data) {

      }
      return response;
    },
    staleTime: 0,
    retry: 3,
  });

  // Fetch user acknowledgments using React Query
  const {
    data: acknowledgmentsResponse,
    isLoading: acknowledgmentsLoading,
    error: acknowledgmentsError,
    refetch: refetchAcknowledgments
  } = useQuery({
    queryKey: ['acknowledgments', user?.id || user?._id],
    queryFn: async () => {
      if (!user?.id && !user?._id) return { items: [] };

      const response = await acknowledgmentService.getAcknowledgments();

      return response;
    },
    enabled: !!user,
    staleTime: 0,
    retry: 2,
  });

  // Handle different response structures with pagination
  const policiesList = React.useMemo(() => {
    if (!policiesResponse) {
      setPoliciesTotal(0);
      setPoliciesTotalPages(1);
      return [];
    }



    let policies = [];
    let total = 0;
    let totalPages = 1;

    // Handle different response structures
    if (policiesResponse.data) {
      // Check for { data: { policies: [...], pagination: {...} } }
      if (policiesResponse.data.policies && Array.isArray(policiesResponse.data.policies)) {
        policies = policiesResponse.data.policies;
        total = policiesResponse.data.pagination?.total || policies.length;
        totalPages = policiesResponse.data.pagination?.totalPages || Math.ceil(total / policiesItemsPerPage) || 1;
      }
      // Check for { data: { data: [...], pagination: {...} } }
      else if (policiesResponse.data.data && Array.isArray(policiesResponse.data.data)) {
        policies = policiesResponse.data.data;
        total = policiesResponse.data.pagination?.total || policies.length;
        totalPages = policiesResponse.data.pagination?.totalPages || Math.ceil(total / policiesItemsPerPage) || 1;
      }
      // Check for { data: [...], pagination: {...} }
      else if (Array.isArray(policiesResponse.data)) {
        policies = policiesResponse.data;
        total = policiesResponse.pagination?.total || policies.length;
        totalPages = policiesResponse.pagination?.totalPages || Math.ceil(total / policiesItemsPerPage) || 1;
      }
    }
    // Check for direct array structure
    else if (Array.isArray(policiesResponse)) {
      policies = policiesResponse;
      total = policies.length;
      totalPages = Math.ceil(total / policiesItemsPerPage) || 1;
    }
    // Check for { policies: [...], pagination: {...} }
    else if (policiesResponse.policies && Array.isArray(policiesResponse.policies)) {
      policies = policiesResponse.policies;
      total = policiesResponse.pagination?.total || policies.length;
      totalPages = policiesResponse.pagination?.totalPages || Math.ceil(total / policiesItemsPerPage) || 1;
    }

    // Update pagination state
    setPoliciesTotal(total);
    setPoliciesTotalPages(totalPages);

    console.log('Parsed policies:', { 
      policiesCount: policies.length, 
      total, 
      totalPages, 
      currentPage: policiesCurrentPage, 
      itemsPerPage: policiesItemsPerPage 
    });

    return policies;
  }, [policiesResponse, policiesItemsPerPage, policiesCurrentPage]);

  let acknowledgments = [];
  if (acknowledgmentsResponse) {
    // Handle paginated response structure: { items: [...], pagination: {...} }
    if (acknowledgmentsResponse.items && Array.isArray(acknowledgmentsResponse.items)) {
      acknowledgments = acknowledgmentsResponse.items;
    } else if (acknowledgmentsResponse.data && Array.isArray(acknowledgmentsResponse.data)) {
      acknowledgments = acknowledgmentsResponse.data;
    } else if (Array.isArray(acknowledgmentsResponse)) {
      acknowledgments = acknowledgmentsResponse;
    }
  }

  console.log('Processed Policies:', policiesList);
  console.log('Processed Acknowledgments:', acknowledgments);
  console.log('🔄 Current State:', {
    policiesLoading,
    policiesError,
    policiesTotal,
    policiesTotalPages,
    policiesCurrentPage,
    policiesListLength: policiesList.length,
    acknowledgmentsCount: acknowledgments.length
  });

  // Map policies with acknowledgment status
  const policiesWithAcknowledgment = policiesList.map(policy => {
    const policyId = (policy.id || policy._id)?.toString();
    const acknowledgment = acknowledgments.find(ack => {
      const ackPolicyId = (ack.policyId || (ack.policy && (ack.policy.id || ack.policy._id || ack.policy)))?.toString();
      // Compare as strings to handle ObjectId vs string matching
      return ackPolicyId === policyId || ackPolicyId === (policy.id || policy._id)?.toString();
    });
    const isAcknowledged = !!acknowledgment && acknowledgment.status === 'acknowledged';
    return {
      ...policy,
      isAcknowledged: isAcknowledged,
      acknowledgedDate: acknowledgment?.acknowledgedDate || acknowledgment?.createdAt || null,
    };
  });

  const loading = policiesLoading || acknowledgmentsLoading;

  // Acknowledge policy mutation using React Query
  const acknowledgeMutation = useMutation({
    mutationFn: async (policyId) => {
      return await acknowledgmentService.createAcknowledgment({
        policyId: policyId,
        action: 'acknowledge'
      });
    },
    onSuccess: async () => {
      // Invalidate and refetch acknowledgments and stats to update UI immediately
      queryClient.invalidateQueries({
        queryKey: ['acknowledgments', user?.id || user?._id],
        exact: false
      });
      queryClient.invalidateQueries({ queryKey: ['policies', 'my-stats'] });

      // Explicitly refetch to ensure UI updates
      try {
        await Promise.all([refetchAcknowledgments(), refetchStats()]);
      } catch (error) {

      }

      alert('Policy acknowledged successfully!');
    },
    onError: (error) => {

      alert('Failed to acknowledge policy. Please try again.');
    },
  });


  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-[#0F2942] bg-blue-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'active': return 'text-[#0F2942] bg-blue-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      case 'active': return <PlayCircle className="w-4 h-4" />;
      case 'inactive': return <PauseCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const handleAcknowledgePolicy = (policyId) => {
    acknowledgeMutation.mutate(policyId);
  };

  const renderPolicies = () => (
    <div className="space-y-4">
      {/* Policies Overview */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="mb-4">
          <h3 className="text-[16px] font-semibold text-slate-700">Policies</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <FileText className="w-4 h-4 text-[#0F2942]" />
              <span className="text-sm font-medium text-gray-700">Total</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpiStats.total}</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700">Acknowledged</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpiStats.acknowledged}</p>
          </div>
          <div className="p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Clock className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-gray-700">Pending</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpiStats.pending}</p>
          </div>
        </div>

        {/* Policies List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Loading policies...</h3>
            <p className="text-xs text-gray-500 mt-1">
              Page {policiesCurrentPage} • Status: Active
            </p>
          </div>
        ) : policiesError || acknowledgmentsError ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-600 font-semibold">Error loading policies</p>
              {policiesError && <p className="text-xs text-red-500 mt-1">{policiesError.message}</p>}
              {acknowledgmentsError && <p className="text-xs text-red-500 mt-1">{acknowledgmentsError.message}</p>}
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              >
                Reload Page
              </button>
            </div>
          </div>
        ) : policiesWithAcknowledgment.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No active policies found.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
            {policiesWithAcknowledgment.map((policy) => (
              <div key={policy.id || policy._id} className="h-full p-4 border border-slate-200 rounded-md bg-white hover:shadow-sm transition-shadow flex flex-col">
                <div className="flex items-start justify-between mb-3 gap-3 min-h-[84px]">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2 flex-wrap">
                      <h4
                        className="text-[16px] font-semibold text-slate-700 capitalize line-clamp-2 leading-5"
                        title={formatLabel(policy.title) || 'Untitled Policy'}
                      >
                        {formatLabel(policy.title) || 'Untitled Policy'}
                      </h4>
                      <span className="px-2 py-1 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700 capitalize flex-shrink-0">
                        {formatLabel(policy.category) || 'General'}
                      </span>
                    </div>
                    <p
                      className="text-[13px] text-slate-600 mb-3 line-clamp-2"
                      title={getPlainTextFromHtml(policy.description) || 'No description available'}
                    >
                      {getPlainTextFromHtml(policy.description) || 'No description available'}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-[12px] text-slate-500">
                      <span><span className="font-semibold text-slate-600">Effective:</span> {formatDate(policy.effectiveDate, { includeTime: false })}</span>
                      <span><span className="font-semibold text-slate-600">Updated:</span> {formatDate(policy.updatedAt || policy.createdAt, { includeTime: false })}</span>
                      <span><span className="font-semibold text-slate-600">Owner:</span> {formatLabel(policy.updatedBy || policy.createdBy) || 'N/A'}</span>
                      <span><span className="font-semibold text-slate-600">Policy ID:</span> {policy.id || policy._id || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[12px] font-medium capitalize ${getStatusColor(policy.isAcknowledged ? 'completed' : 'pending')}`}>
                      {getStatusIcon(policy.isAcknowledged ? 'completed' : 'pending')}
                      <span className="ml-1 capitalize">{policy.isAcknowledged ? 'Acknowledged' : 'Pending'}</span>
                    </span>
                    {policy.isAcknowledged && policy.acknowledgedDate && (
                      <span className="text-[11px] text-slate-500">
                        {formatDate(policy.acknowledgedDate, { includeTime: false })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setSelectedPolicy(policy);
                        setShowPolicyModal(true);
                      }}
                      className="flex items-center space-x-1 px-3 py-1.5 text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-50 text-[12px] font-medium"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!policy.isAcknowledged && (
                      <button
                        onClick={() => handleAcknowledgePolicy(policy.id || policy._id)}
                        disabled={acknowledgeMutation.isPending}
                        className="flex items-center space-x-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-[12px] font-medium"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>{acknowledgeMutation.isPending ? 'Acknowledging...' : 'Acknowledge'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && policiesTotal > 0 && (
          <div className="mt-6 bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <PaginationControls
              currentPage={policiesCurrentPage}
              totalPages={policiesTotalPages}
              totalItems={policiesTotal}
              limit={policiesItemsPerPage}
              onPageChange={(page) => {
                if (page < 1 || page > policiesTotalPages || page === policiesCurrentPage) return;
                setPoliciesCurrentPage(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              onLimitChange={(newLimit) => {
                // Optional: handle limit changes if needed
              }}
              hasNextPage={policiesCurrentPage < policiesTotalPages}
              hasPrevPage={policiesCurrentPage > 1}
              border={false}
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderPolicyModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-xl">
        <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h3 className="text-xl font-semibold text-gray-900 capitalize break-words">
                {formatLabel(selectedPolicy?.title) || 'Policy Details'}
              </h3>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                <span className="break-words">
                  <span className="font-medium text-gray-700">Effective:</span>{' '}
                  {formatDate(selectedPolicy?.effectiveDate, { includeTime: false }) || 'N/A'}
                </span>
                <span className="break-words">
                  <span className="font-medium text-gray-700">Category:</span>{' '}
                  {formatLabel(selectedPolicy?.category) || 'N/A'}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowPolicyModal(false)}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0 rounded-md p-1 hover:bg-gray-100"
              aria-label="Close policy details"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Description</h4>
              <div className="ql-snow rounded-lg border border-gray-100 bg-gray-50/50">
                <div
                  className={`ql-editor !border-0 !p-4 !min-h-0 ${RICH_TEXT_DISPLAY_CLASS}`}
                  dangerouslySetInnerHTML={{
                    __html: prepareDescriptionForDisplay(selectedPolicy?.description),
                  }}
                />
              </div>
            </div>
            {selectedPolicy?.content && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Content</h4>
                <div className="ql-snow rounded-lg border border-gray-100 bg-gray-50/50">
                  <div
                    className={`ql-editor !border-0 !p-4 !min-h-0 ${RICH_TEXT_DISPLAY_CLASS}`}
                    dangerouslySetInnerHTML={{
                      __html: prepareDescriptionForDisplay(selectedPolicy.content),
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="font-sans text-gray-700">
      <div>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 mb-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0F2942] text-white flex items-center justify-center shadow-sm">
              <FileText className="w-6 h-6 stroke-[2]" />
            </div>
            <div>
              <h1 className="text-[22px] font-bold text-[#0F2942] mb-0.5 leading-tight">Policies</h1>
              <p className="text-[12px] text-[#64748B]">Review and acknowledge company policies</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {renderPolicies()}

        {/* Modals */}
        {showPolicyModal && renderPolicyModal()}
      </div>
    </div>
  );
};

export default PoliciesCompliance;
